import nanoajax from 'nanoajax'

class Cart {
  async getProductTags(productHandle) {
    try {
      const storageKey = `product_tags_${productHandle}`
      const cachedTags = sessionStorage.getItem(storageKey)

      if (cachedTags) {
        return JSON.parse(cachedTags)
      }

      const response = await fetch(`/products/${productHandle}.js`)
      const product = await response.json()
      const tags = product.tags || []

      sessionStorage.setItem(storageKey, JSON.stringify(tags))

      return tags
    } catch (error) {
      console.error('Error fetching product tags:', error)
      return []
    }
  }

  async validateCartLines (cart) {
    if (!cart.items) return cart

    let newCart = cart

    for (const lineItem of cart.items) {
      const isInBundle = lineItem.properties.hasOwnProperty('_bundleHandle')

      if (!isInBundle) {
        const productTags = await this.getProductTags(lineItem.handle)
        const hasSubscriptionTag = productTags.includes('Subscription')
        const hasStoreLocatorProduct = productTags.includes('store-locator')

        if (hasSubscriptionTag || hasStoreLocatorProduct) {
          await this.change({
            quantity: 0,
            id: lineItem.variant_id
          })
          newCart = await this.get()
        }
      }
    }

    return newCart
  }

  get () {
    return new Promise(resolve => {
      nanoajax.ajax({
        url: '/cart.js',
        method: 'get'
      }, async (code, responseText, request) => {
        const cart = JSON.parse(responseText)
        const validatedCart = await this.validateCartLines(cart)
        resolve(validatedCart)
      })
    })
  }

  customCart () {
    return new Promise(resolve => {
      nanoajax.ajax({
        url: '/cart?view=endpoint',
        method: 'get'
      }, async (code, responseText, request) => {
        const cart = JSON.parse(responseText)
        const validatedCart = await this.validateCartLines(cart)
        resolve(validatedCart)
      })
    })
  }

  generateShopifyCartChangeBody ({ id, quantity }) {
    return `quantity=${quantity}&id=${id}`
  }

  change (data) {
    return new Promise((resolve, reject) => {
      nanoajax.ajax({
        url: '/cart/change.js',
        method: 'post',
        body: this.generateShopifyCartChangeBody(data)
      }, async (code, responseText, request) => {
        const cart = JSON.parse(responseText)
        if (Number(code) !== 200) {
          cart['errors'] = [cart.description]
        }
        const validatedCart = await this.validateCartLines(cart)
        resolve(validatedCart)
      })
    })
  }

  generateShopifyCartUpdateBody (data) {
    return data instanceof Array
      ? data.map(({id, quantity}) => `updates[${id}]=${quantity}`).join('&')
      : `updates[${data.id}]=${data.quantity}`
  }

  update (data) {
    return new Promise((resolve, reject) => {
      nanoajax.ajax({
        url: '/cart/update.js',
        method: 'post',
        body: this.generateShopifyCartUpdateBody(data)
      }, async (code, responseText, request) => {
        const cart = JSON.parse(responseText)
        if (Number(code) !== 200) {
          cart['errors'] = [cart.description]
        }
        const validatedCart = await this.validateCartLines(cart)
        resolve(validatedCart)
      })
    })
  }

  generateShopifyCartAddBody ({ id, quantity, selling_plan, properties = {} }) {
    let query = `quantity=${quantity}&id=${id}`
    if (selling_plan) {
      query += `quantity=${quantity}&id=${id}&selling_plan=${selling_plan}`
    }
    if (properties && Object.keys(properties).length) {
      return Object.keys(properties).reduce((str, key) => {
        str += '&'
        str += `properties[${key}]=${properties[key]}`
        return str
      }, query)
    }
    return query
  }

  /**
   * Adds a product to the Shopify cart
   *
   * @param {Object} data {id: <id>, quantity: <Number>}
   */
  add (data) {
    return new Promise(resolve => {
      nanoajax.ajax({
        url: '/cart/add.js',
        method: 'post',
        body: this.generateShopifyCartAddBody(data)
      }, (code, responseText, request) => {
        const cart = JSON.parse(responseText)
        resolve(cart)
        this.validateCartLines(cart)
      })
    })
  }
}

export default new Cart()
